import { Paperclip, X } from "lucide-react";
import React, { useContext, useEffect, useState } from "react";
import { useForm, yupResolver } from "@mantine/form";
import {
  Button,
  Group,
  Modal,
  Radio,
  RadioGroup,
  Select,
  Space,
  Textarea,
  TextInput,
} from "@mantine/core";
import dayjs from "dayjs";
import { Navigate } from "react-router-dom";
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import { PickerInline } from "filestack-react";
import Announcement, {
  AnnouncementType,
  AnnouncementTypeEnums,
} from "../services/model/Announcement";
import useCreateAnnouncementApiService from "../services/api/CreateAnnouncementApiService";
import useUpdateAnnouncementApiService from "../services/api/UpdateAnnouncementApiService";
import { AuthContext } from "../../../providers/AuthProvider";
import { HttpClientError } from "../../../providers/HttpClient";
import useFilestackConfigApiService from "../services/api/FilestackConfigApiService";
import AnnouncementValidationSchema from "../services/validator/AnnouncementValidationSchema";
import TagMultiSelect from "../../tags/elements/TagMultiSelect";
import SegmentSelector from "../../../common/components/SegmentSelector";
import { queryClient } from "../../../App";
import ScheduleDateTimeInput from "../../../themes/elements/ScheduleDateTimeInput";
import PublishAnnouncementButton from "../elements/PublishAnnouncementButton";
import FileStackUploadResponse from "../services/model/FileStackUploadResponse";
import Colors from "../../../themes/Colors";
import Dictionary from "../../../utils/Dictionary";
import useAnnouncementTypeListApiService from "../services/api/AnnouncementTypeListApiService";
import ProgressIndicator from "../../../themes/components/ProgressIndicator";
import FeatureFlags from "../../../common/feature-flagging/FeatureFlags";
import { useEnabledFeatureFlag } from "../../../common/feature-flagging/FeatureFlagProvider";
import { useToasts } from "../../../providers/notifications/ToastsContext";
import { stringContainsAnyCharacters } from "../../../utils/Text";
import SecureAnnouncementUpload from "./SecureAnnouncementUpload";

type AnnouncementFormProps = {
  onSuccess?: () => void;
  onFailure?: () => void;
  values?: Announcement;
};

type FileStackResponse = {
  filesFailed: [];
  filesUploaded: FileStackUploadResponse[];
};

// Announcement should only acccept the files mentioned below

const ANNOUNCEMENT_DOCUMENT_FILE_EXTENSION = ["application/pdf"];

const ANNOUNCEMENT_IMAGE_FILE_EXTENSION = ["image/jpeg", "image/png"];

const ANNOUNCEMENT_VIDEO_FILE_EXTENSION = ["video/mp4"];

const ANNOUNCEMENT_AUDIO_FILE_EXTENSION = [
  "audio/mpeg",
  "audio/ogg",
  "audio/amr",
];

const ANNOUNCEMENT_ACCEPT_FILE_EXTENSION = [
  ...ANNOUNCEMENT_DOCUMENT_FILE_EXTENSION,
  ...ANNOUNCEMENT_IMAGE_FILE_EXTENSION,
  ...ANNOUNCEMENT_VIDEO_FILE_EXTENSION,
  ...ANNOUNCEMENT_AUDIO_FILE_EXTENSION,
];

const MEDIA_TYPE_PDF = "pdf";
const MEDIA_TYPE_IMAGE = "image";
const MEDIA_TYPE_VIDEO = "video";
const MEDIA_TYPE_AUDIO = "audio";

// annoucnemt should not accept files larger than 16MB
const ANNOUNCEMENT_ACCEPT_FILE_SIZE = 16 * 1024 * 1024;
const ANNOUNCEMENT_ACCEPT_IMAGE_FILE_SIZE = 5 * 1024 * 1024;

const specialWhatsappCharacters = '_*~`-">';

const AnnouncementForm: React.FC<AnnouncementFormProps> = ({
  onSuccess,
  onFailure,
  values,
}) => {
  const announcementTypesEnabled = useEnabledFeatureFlag(
    FeatureFlags.ANNOUNCMENT_TYPES
  );

  const gcpUploadsEnabled = useEnabledFeatureFlag(
    FeatureFlags.ANNOUNCEMENTS_ATTACHMENTS_ENABLE_GCP
  );

  // Check if segmentation feature is enabled for announcements
  const segmentsEnabled = useEnabledFeatureFlag(FeatureFlags.SEGMENTS);
  const segmentsAnnouncementsEnabled = useEnabledFeatureFlag(
    FeatureFlags.SEGMENTS_ANNOUNCEMENTS
  );
  const showSegmentationToggle =
    segmentsEnabled && segmentsAnnouncementsEnabled;

  const auth = useContext(AuthContext);

  // State for targeting type and segments
  const [targetingType, setTargetingType] = useState<"tags" | "segments">(
    values?.targeting_type || "tags"
  );
  const [segmentIds, setSegmentIds] = useState<string[]>(
    values?.segments ? values.segments.map((s) => s.id.toString()) : []
  );

  useEffect(() => {
    if (!showSegmentationToggle) {
      setTargetingType("tags");
      setSegmentIds([]);
    }
  }, [showSegmentationToggle]);

  const { showToast } = useToasts();

  // Helper function to strip query parameters (signed URL tokens) from media URL
  const cleanMediaUrl = (mediaUrl: string | null): string | null => {
    if (!mediaUrl) return null;
    // Remove query parameters and fragments to get the base URL
    return mediaUrl.split("?")[0].split("#")[0];
  };

  // Helper function to extract filename from media URL
  const extractFileNameFromUrl = (mediaUrl: string | null): string | null => {
    if (!mediaUrl) return null;

    try {
      // Remove query parameters and fragments
      const cleanUrl = cleanMediaUrl(mediaUrl);

      // Extract filename from path (works for both GCS paths and full URLs)
      const pathParts = cleanUrl?.split("/") || [];
      const filename = pathParts[pathParts.length - 1];

      // Return the filename if it exists and looks valid
      if (filename && filename.length > 0) {
        // Decode any URL encoding
        return decodeURIComponent(filename);
      }
    } catch (error) {
      // If there's any error parsing, just return null
      // Silently handle the error as this is not critical
    }

    return null;
  };

  const [opened, setOpened] = useState(false);

  const [titleError, setTitleError] = useState<string | null>(null);

  const [submitButtonDisabled, setSubmitButtonDisabled] = useState(false);

  // Temporary: Using simple API key approach while migrating to GCP uploads
  const MemoizedPickerInline = React.memo(PickerInline); // prevents re-rendering of the picker

  // Use service layer following architecture patterns
  const { data: filestackConfig } = useFilestackConfigApiService();

  const getMediaType = (filename: string): string => {
    let type: string;

    switch (true) {
      case ANNOUNCEMENT_DOCUMENT_FILE_EXTENSION.includes(filename):
        type = MEDIA_TYPE_PDF;
        break;
      case ANNOUNCEMENT_IMAGE_FILE_EXTENSION.includes(filename):
        type = MEDIA_TYPE_IMAGE;
        break;

      case ANNOUNCEMENT_VIDEO_FILE_EXTENSION.includes(filename):
        type = MEDIA_TYPE_VIDEO;
        break;

      case ANNOUNCEMENT_AUDIO_FILE_EXTENSION.includes(filename):
        type = MEDIA_TYPE_AUDIO;
        break;

      default:
        type = "";
        break;
    }

    return type;
  };

  const [announcementTypes, setAnnouncementTypes] = useState<
    AnnouncementType[]
  >([]);

  const { isLoading: isAnnouncementTypesLoading, data: announcementTypesData } =
    useAnnouncementTypeListApiService();

  useEffect(() => {
    if (announcementTypesData) {
      setAnnouncementTypes(
        [...announcementTypesData].sort((a, b) => a.id - b.id)
      );
    }

    return () => {
      setAnnouncementTypes([]);
    };
  }, [announcementTypesData]);

  const {
    isLoading: isCreateLoading,
    isSuccess: isCreateSuccess,
    isError: isCreateFailure,
    error: createError,
    mutate: onCreate,
  } = useCreateAnnouncementApiService();

  const {
    isLoading: isUpdateLoading,
    isSuccess: isUpdateSuccess,
    isError: isUpdateFailure,
    error: updateError,
    mutate: onUpdate,
  } = useUpdateAnnouncementApiService();

  const today = new Date();
  const expireDate = dayjs(today).add(7, "day").toDate(); // Changed from 2 months to 7 days to match default preset

  const [tagIds, setTagIds] = useState<number[]>([]);

  useEffect(() => {
    if (isCreateFailure) {
      showToast({
        strapiId: "announcement_create_failed",
        data: { message: HttpClientError(createError) },
      });
    } else if (isUpdateFailure) {
      showToast({
        strapiId: "announcement_update_failed",
        data: { message: HttpClientError(updateError) },
      });
    }

    if (onFailure) {
      onFailure();
    }
  }, [isCreateFailure, createError, updateError, isUpdateFailure]);

  // Handle create success
  useEffect(() => {
    if (isCreateSuccess && auth) {
      // Show toast and close modal immediately for better UX
      showToast({ strapiId: "announcement_created" });
      if (onSuccess) {
        onSuccess();
      }
      // Invalidate in background - don't wait for refetch to complete
      queryClient.invalidateQueries(["AnnouncementListApiService"]);
    }
  }, [isCreateSuccess, auth]);

  // Handle update success
  useEffect(() => {
    if (isUpdateSuccess && auth) {
      // Show toast and close modal immediately for better UX
      showToast({ strapiId: "announcement_updated" });
      if (onSuccess) {
        onSuccess();
      }
      // Invalidate in background - don't wait for refetch to complete
      queryClient.invalidateQueries([
        "AnnouncementDetailsApiService",
        values?.id,
      ]);
      queryClient.invalidateQueries(["AnnouncementListApiService"]);
    }
  }, [isUpdateSuccess, auth]);

  const form = useForm({
    schema: yupResolver(AnnouncementValidationSchema),
    initialValues: {
      id: values?.id || undefined,
      title: values?.title || "",
      announcementType: values?.announcement_type ?? null,
      content: values?.content || "",
      tags: values?.tags ? values.tags.map((it) => it.id.toString()) : [],
      status: values?.status || "draft",
      scheduledStartDate: values?.scheduled_start_date
        ? dayjs(values?.scheduled_start_date).toDate()
        : today,
      scheduledEndDate: values?.scheduled_end_date
        ? dayjs(values?.scheduled_end_date).toDate()
        : expireDate,
      mediaUrl: cleanMediaUrl(values?.media_url || null) || null,
      mediaType: values?.media_type || null,
      mediaSize: values?.media_size_kb || null,
      mediaFileName: extractFileNameFromUrl(values?.media_url || null), // Extract filename from URL
      extra_fields:
        values?.extra_fields || values?.announcement_type?.extra_fields || [],
    },
  });

  useEffect(() => {
    setTagIds(form.values.tags.map((tagId) => +tagId));
  }, [form.values.tags]);

  useEffect(() => {
    if (
      announcementTypes &&
      announcementTypes.length > 0 &&
      !form.values.announcementType
    ) {
      form.setFieldValue("announcementType", announcementTypes[0]);
      form.setFieldValue(
        "extra_fields",
        announcementTypes[0].extra_fields || []
      );
    }
  }, [announcementTypes, form.values.announcementType, form.setFieldValue]);

  const onSubmitHandler = (formValues: typeof form.values): void => {
    const {
      content,
      status,
      scheduledStartDate,
      scheduledEndDate,
      title,
      mediaUrl,
      mediaType,
      mediaSize,
    } = formValues;

    // Validate announcement type exists before submitting
    if (!formValues.announcementType?.id) {
      showToast({
        strapiId: "announcement_type_required",
        data: { message: "Announcement type is required." },
      });
      return;
    }

    // Clean media URL to strip any signed URL tokens before submitting
    const cleanedMediaUrl = cleanMediaUrl(mediaUrl);

    // Prepare tags or segments based on targeting type
    const requestData = {
      content,
      status,
      title,
      announcement_type_id: formValues.announcementType.id,
      media_url: cleanedMediaUrl || null,
      media_type: mediaType || null,
      media_size_kb: mediaSize || null,
      targeting_type: targetingType,
      tags:
        targetingType === "tags" ? formValues.tags.map((it) => Number(it)) : [],
      segments:
        targetingType === "segments" ? segmentIds.map((it) => Number(it)) : [],
      scheduled_start_date: scheduledStartDate.toISOString(),
      scheduled_end_date: scheduledEndDate.toISOString(),
      extra_fields: formValues?.extra_fields || [],
    };

    if (values) {
      onUpdate({
        id: values.id,
        ...requestData,
        date_published: Object.is(status, "published")
          ? today.toISOString()
          : undefined,
      });
    } else {
      onCreate({
        ...requestData,
        employer_id: auth?.employer.id || 0,
        created_by_id: auth?.id || 0,
        date_published: Object.is(formValues.status, "published")
          ? today.toISOString()
          : undefined,
      });
    }
  };

  const handleSaveDraft = (): void => {
    const { hasErrors } = form.validate();
    if (!hasErrors) {
      onSubmitHandler({ ...form.values, status: "draft" });
    }
  };

  const handlePublish = (): void => {
    const { hasErrors } = form.validate();
    if (!hasErrors) {
      onSubmitHandler({ ...form.values, status: "published" });
    }
  };

  const removeMedia = (): void => {
    form.setFieldValue("mediaUrl", "");
    form.setFieldValue("mediaType", "");
    form.setFieldValue("mediaSize", 0);
    form.setFieldValue("mediaFileName", null);
  };

  const filterCharacters = (): void => {
    if (
      stringContainsAnyCharacters(form.values.title, specialWhatsappCharacters)
    ) {
      setTitleError(
        `Title cannot include the following characters: ${specialWhatsappCharacters}`
      );
      setSubmitButtonDisabled(true);
    } else {
      setTitleError(null);
      setSubmitButtonDisabled(false);
    }
  };

  if (!auth) {
    return <Navigate replace to="/login" />;
  }

  const announcementTypeOptions = announcementTypes.map((announcementType) => ({
    value: announcementType.id.toString(),
    label: announcementType.name,
  }));

  return (
    <form id="create-announcement-form" style={{ width: "100%" }}>
      {isAnnouncementTypesLoading && <ProgressIndicator />}
      {announcementTypes && form.values.announcementType && (
        <>
          <Modal
            size="50%"
            opened={opened}
            onClose={() => setOpened(false)}
            closeOnClickOutside
            styles={{
              body: {
                padding: 0,
              },
            }}
          >
            {opened &&
              filestackConfig && ( // prevent tying the render based on modal's internal state changes
                <MemoizedPickerInline
                  key="filestack-announcement-attachment-picker"
                  pickerOptions={{
                    fromSources: ["local_file_system", "webcam"],
                    accept: ANNOUNCEMENT_ACCEPT_FILE_EXTENSION,
                    maxSize: ANNOUNCEMENT_ACCEPT_FILE_SIZE,
                    errorsTimeout: 10000,
                    onFileSelected: (file: any) => {
                      if (
                        ANNOUNCEMENT_IMAGE_FILE_EXTENSION.includes(
                          file.mimetype
                        )
                      ) {
                        if (file.size > ANNOUNCEMENT_ACCEPT_IMAGE_FILE_SIZE) {
                          throw new Error(
                            "Image file size exceeds the limit of 5MB"
                          );
                        }
                      }

                      if (
                        [
                          ...ANNOUNCEMENT_DOCUMENT_FILE_EXTENSION,
                          ...ANNOUNCEMENT_VIDEO_FILE_EXTENSION,
                          ...ANNOUNCEMENT_AUDIO_FILE_EXTENSION,
                        ].includes(file.mimetype)
                      ) {
                        if (file.size > ANNOUNCEMENT_ACCEPT_FILE_SIZE) {
                          let tempMediaType = "";

                          if (
                            ANNOUNCEMENT_DOCUMENT_FILE_EXTENSION.includes(
                              file.mimetype
                            )
                          ) {
                            tempMediaType = "PDF";
                          } else if (
                            ANNOUNCEMENT_VIDEO_FILE_EXTENSION.includes(
                              file.mimetype
                            )
                          ) {
                            tempMediaType = "Video";
                          } else if (
                            ANNOUNCEMENT_AUDIO_FILE_EXTENSION.includes(
                              file.mimetype
                            )
                          ) {
                            tempMediaType = "Audio";
                          }
                          throw new Error(
                            `${tempMediaType} File size exceeds the limit of 16MB.`
                          );
                        }
                      }
                    },
                  }}
                  apikey={filestackConfig.apiKey}
                  onSuccess={(res: FileStackResponse) => {
                    if (res.filesUploaded && res.filesUploaded.length > 0) {
                      const file = res.filesUploaded[0];
                      form.setFieldValue("mediaUrl", file.url);
                      form.setFieldValue(
                        "mediaType",
                        getMediaType(file.mimetype)
                      );
                      form.setFieldValue("mediaSize", file.size);
                      form.setFieldValue("mediaFileName", file.filename);

                      setOpened(false);
                      showToast({ strapiId: "announcement_file_uploaded" });
                    }
                  }}
                />
              )}
          </Modal>
          {announcementTypesEnabled && (
            <Select
              required
              label="Select an Announcement Template"
              data={announcementTypeOptions}
              value={form.values.announcementType?.id.toString() || ""}
              onChange={(value) => {
                const announcementTypeData = announcementTypes.find(
                  (announcementType) => announcementType.id === Number(value)
                );
                form.setFieldValue(
                  "announcementType",
                  announcementTypeData || null
                );
                form.setFieldValue(
                  "extra_fields",
                  announcementTypeData?.extra_fields || []
                );
              }}
            />
          )}
          <Space h="xl" />
          <TextInput
            required
            label={
              Dictionary.Announcements[
                form.values.announcementType?.name as AnnouncementTypeEnums
              ]?.titleField || "Title"
            }
            type="text"
            description={
              form.values.title.length
                ? `number of characters remaining: ${
                    50 - form.values.title.length
                  }`
                : ""
            }
            onInput={() => filterCharacters()}
            onKeyUp={() => filterCharacters()}
            error={titleError}
            {...form.getInputProps("title")}
            radius="md"
            maxLength={50}
          />
          <Space h="xl" />
          <Textarea
            required
            label={
              Dictionary.Announcements[
                form.values.announcementType?.name as AnnouncementTypeEnums
              ]?.bodyField || "Message"
            }
            description={
              form.values.content.length ? (
                <span
                  style={{
                    color:
                      form.values.content.length !== 900
                        ? Colors.description.primary
                        : Colors.description.error,
                  }}
                >
                  number of characters remaining:{" "}
                  {900 - form.values.content.length}
                </span>
              ) : (
                ""
              )
            }
            {...form.getInputProps("content")}
            radius="md"
            minRows={3}
            maxLength={900}
          />

          {form.values.announcementType?.extra_fields?.map((field, index) => {
            switch (field.type) {
              case "input":
                return (
                  <div key={field.label}>
                    <Space h="xl" />
                    <TextInput
                      required
                      label={field.label}
                      radius="md"
                      maxLength={field.max_length || 50}
                      value={form.values?.extra_fields?.[index]?.value || ""}
                      onChange={({
                        target,
                      }: React.ChangeEvent<HTMLInputElement>) => {
                        const tempExtraFields = form.values.extra_fields;
                        tempExtraFields[index].value = target.value;
                        form.setFieldValue("extra_fields", tempExtraFields);
                      }}
                    />
                  </div>
                );
              default:
                break;
            }
            return null;
          })}

          <Space h="xl" />

          {gcpUploadsEnabled ? (
            <SecureAnnouncementUpload
              onSuccess={(fileData) => {
                form.setFieldValue("mediaUrl", fileData.url);
                form.setFieldValue("mediaType", fileData.mediaType);
                form.setFieldValue("mediaSize", fileData.sizeKB);
                form.setFieldValue("mediaFileName", fileData.fileName);
              }}
              onRemove={() => removeMedia()}
              disabled={false}
              currentFileName={form.values.mediaFileName || undefined}
            />
          ) : (
            <Group position="apart" spacing="md">
              {form.values.mediaUrl && (
                <Group>
                  <TextInput
                    placeholder="Enter file name"
                    icon={<Paperclip size={14} />}
                    value={form.values.title}
                    onChange={({
                      target,
                    }: React.ChangeEvent<HTMLInputElement>) =>
                      form.setFieldValue("title", target.value)
                    }
                  />
                </Group>
              )}
              <Button
                leftIcon={
                  form.values.mediaUrl ? (
                    <X size={22} />
                  ) : (
                    <Paperclip size={22} />
                  )
                }
                onClick={
                  form.values.mediaUrl
                    ? () => removeMedia()
                    : () => setOpened(true)
                }
                color="jem-theme"
              >
                {form.values.mediaUrl ? "Remove" : "Attach"} Media
              </Button>
            </Group>
          )}

          <Space h="xl" />
          <ScheduleDateTimeInput
            form={form}
            description="Choose how long to display the announcement"
          />

          <Space h="xl" />

          {/* Audience Selection */}
          <div>
            <div className="font-medium text-[14px] text-[#062133] mb-2">
              Select employee audience <span className="text-[#ED7687]">*</span>
            </div>
            <p className="text-[12px] text-[#839099] mb-3">
              Choose which employee groups should receive this announcement
            </p>

            {/* Targeting Type Toggle - Only show if segmentation is enabled */}
            {showSegmentationToggle && (
              <>
                <RadioGroup
                  value={targetingType}
                  onChange={(value: "tags" | "segments") =>
                    setTargetingType(value)
                  }
                  color="jem-theme"
                  size="xs"
                  orientation="horizontal"
                  spacing="sm"
                >
                  <Radio value="segments" label="Segments" />
                  <Radio value="tags" label="Tags" />
                </RadioGroup>
                <Space h="md" />
              </>
            )}

            {/* Conditional Rendering based on targeting type */}
            {showSegmentationToggle && targetingType === "segments" ? (
              <SegmentSelector
                value={segmentIds}
                onChange={setSegmentIds}
                label=""
                description=""
                placeholder="Select segments"
              />
            ) : (
              <TagMultiSelect form={form} />
            )}
          </div>

          <Space h="md" />
          <Button
            fullWidth
            color="jem-theme"
            type="button"
            variant="outline"
            className="bg-white background-color-white background-background-white"
            style={{
              backgroundColor: submitButtonDisabled ? "white" : "jem-theme",
              border: "1px solid rgba(108, 108, 127, 0.25)",
              borderRadius: "6px",
            }}
            loading={isCreateLoading || isUpdateLoading}
            disabled={submitButtonDisabled}
            onClick={handleSaveDraft}
          >
            Save Draft and Exit
          </Button>
          <Space h="md" />
          <PublishAnnouncementButton
            onClick={handlePublish}
            tagIds={tagIds}
            segmentIds={segmentIds.map((id) => Number(id))}
            targetingType={targetingType}
            loading={isCreateLoading || isUpdateLoading}
            submitButtonDisabled={submitButtonDisabled}
          />
        </>
      )}
      <Space h="xl" />
      <Space h="xl" />
      <Space h="xl" />
      <Space h="xl" />
      <Space h="xl" />
    </form>
  );
};

export default AnnouncementForm;
